import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, useLang } from "../lib/providers";
import { Plus } from "@phosphor-icons/react";

/**
 * Dropdown selector for VAT rates managed via /vat-rates.
 * Picks rate + note together based on the selected VAT-rate id.
 *
 * Props:
 *   value:    current numeric vat_rate (number or string)
 *   note:     current vat_note (optional, used for matching legacy quotes/invoices)
 *   onChange: (rate: number, note: string) => void — called when user picks a different rate
 *   disabled
 */
export default function VatRatePicker({ value, note = "", onChange, disabled }) {
  const { lang } = useLang();
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api.get("/vat-rates")
      .then((r) => { if (mounted) setRates(r.data || []); })
      .catch(() => { if (mounted) setRates([]); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  // Find matching rate: prefer (rate + note) match, fall back to rate-only match
  const numericValue = Number(value) || 0;
  const matchByBoth = rates.find((r) => Math.abs(r.rate - numericValue) < 0.0001 && (r.note || "") === (note || ""));
  const matchByRate = rates.find((r) => Math.abs(r.rate - numericValue) < 0.0001);
  const selected = matchByBoth || matchByRate || null;

  const onPick = (e) => {
    const id = e.target.value;
    if (id === "__custom__") return; // ignore — placeholder only
    const r = rates.find((x) => x.id === id);
    if (r) onChange(r.rate, r.note || "");
  };

  return (
    <div className="space-y-1.5" data-testid="vat-picker">
      <select
        value={selected?.id || "__custom__"}
        onChange={onPick}
        disabled={disabled || loading}
        data-testid="vat-picker-select"
        className="w-full h-10 px-3 bg-[var(--surface)] border border-[var(--border)] rounded-sm text-sm focus:border-[var(--brand)] focus:ring-2 focus:ring-[var(--brand)]/20 focus:outline-none"
      >
        {!selected && (
          <option value="__custom__">
            {lang === "de" ? `${numericValue}% (manuell)` : `${numericValue}% (manual)`}
          </option>
        )}
        {rates.map((r) => (
          <option key={r.id} value={r.id}>
            {r.label} {r.is_default ? (lang === "de" ? "(Standard)" : "(default)") : ""}
          </option>
        ))}
      </select>

      {selected?.note && (
        <div className="text-xs text-[var(--muted)] leading-relaxed whitespace-pre-wrap" data-testid="vat-picker-note">
          {selected.note}
        </div>
      )}

      {!loading && rates.length === 0 && (
        <Link to="/vat-rates" className="text-xs text-[var(--brand)] hover:underline inline-flex items-center gap-1">
          <Plus size={12} /> {lang === "de" ? "MwSt-Sätze anlegen" : "Add VAT rates"}
        </Link>
      )}
    </div>
  );
}
